import IconButton from "@mui/material/IconButton";
import SendIcon from "@mui/icons-material/Send";
import React from "react";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import axios from 'axios';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Form() {
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [website, setWebsite] = React.useState(""); // honeypot, real users leave this blank
  const [turnstileToken, setTurnstileToken] = React.useState("");
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [alertSeverity, setAlertSeverity] = React.useState("success");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const mountedAtRef = React.useRef(Date.now());
  const turnstileRef = React.useRef(null);
  const turnstileWidgetId = React.useRef(null);

  const handleChange = (event, setStateName) => {
    setStateName(event.target.value);
  };

  // Render the Turnstile widget once its script has loaded on the page
  React.useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current) return;

    let cancelled = false;
    const renderWidget = () => {
      if (cancelled || !window.turnstile || turnstileWidgetId.current) return;
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          renderWidget();
        }
      }, 200);
      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!name || !email || !message) {
      setAlertMessage("Please fill in all fields");
      setAlertSeverity("error");
      setAlertOpen(true);
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setAlertMessage("Please complete the verification checkbox");
      setAlertSeverity("error");
      setAlertOpen(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        "https://kft09pp571.execute-api.us-east-1.amazonaws.com/mailer",
        {
          name: name,
          email: email,
          message: message,
          website: website,
          elapsedMs: Date.now() - mountedAtRef.current,
          turnstileToken: turnstileToken,
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        setAlertMessage("Message sent successfully! We'll get back to you soon.");
        setAlertSeverity("success");
        setAlertOpen(true);
        
        // Clear form
        setName("");
        setEmail("");
        setMessage("");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setAlertMessage("Failed to send message. Please try again later.");
      setAlertSeverity("error");
      setAlertOpen(true);
    } finally {
      setIsSubmitting(false);
      setTurnstileToken("");
      if (window.turnstile && turnstileWidgetId.current) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    }
  };

  return (
    <div className="flex flex-wrap justify-center">
      <div className="w-full lg:w-8/12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 bg-white shadow-xl rounded-lg">
          <div className="flex-auto p-5 lg:p-10">
            <div className="w-full text-center">
              <h3 className="text-3xl font-semibold text-gray-700">Want to Contact Us?</h3>
              <p className="text-lg leading-relaxed mt-4 mb-4 text-gray-600">
                Complete this form to contact the Rise Up Prison Ministry
              </p>
            </div>

            {alertOpen && (
              <Box sx={{ width: "100%", mt: 2 }}>
                <Collapse in={alertOpen}>
                  <Alert
                    severity={alertSeverity}
                    action={
                      <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => setAlertOpen(false)}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    }
                    sx={{ mb: 2 }}
                  >
                    {alertMessage}
                  </Alert>
                </Collapse>
              </Box>
            )}

            <form onSubmit={handleSubmit}>
              <div
                className="flex gap-8 mt-16 mb-12"
                style={{ justifyContent: "space-around" }}
              >
                <TextField
                  id="name-input"
                  label="Name"
                  variant="outlined"
                  value={name}
                  onChange={(event) => handleChange(event, setName)}
                  required
                  disabled={isSubmitting}
                />

                <TextField
                  id="email-input"
                  label="Email"
                  variant="outlined"
                  type="email"
                  value={email}
                  onChange={(event) => handleChange(event, setEmail)}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <TextField
                id="message-input"
                label="Message"
                multiline
                rows={10}
                style={{ width: "100%" }}
                value={message}
                onChange={(event) => handleChange(event, setMessage)}
                required
                disabled={isSubmitting}
              />

              {/* Honeypot field: hidden from real users via CSS, bots that auto-fill forms trip it */}
              <input
                type="text"
                name="website"
                id="website-input"
                value={website}
                onChange={(event) => handleChange(event, setWebsite)}
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: "1px",
                  height: "1px",
                  opacity: 0,
                }}
              />

              {TURNSTILE_SITE_KEY && (
                <div className="flex justify-center mt-6">
                  <div ref={turnstileRef} />
                </div>
              )}

              <div className="flex justify-center mt-10">
                <IconButton
                  aria-label="send"
                  color="secondary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  <SendIcon />
                </IconButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
